import type { ScopeEditorPersistenceState } from '../scopeEditorPersistence';
import type { MappingParameterRepairKind } from '../../../../shared/lib/mappingParameterScopeRequirements';
import {
  PROCESS_CONDITIONS_FIELD_BY_EDITOR_ROW_ID,
  PROJECT_PROPERTIES_FIELD_BY_EDITOR_ROW_ID,
} from '../../../../shared/config/mappingScopeEditorFieldDefaults';
import {
  collectAdditionalCategoryTargets,
  collectConnectorBindingParameters,
} from '../../../../shared/lib/hooks/useParameterAddition';
import { isBoltProfileMappingRow } from '../../../../shared/lib/boltProfileMapping';
import { workflowIncludesIsoOnlySetup } from '../../../../shared/config/workflowUi';
import { type PcfSettingsDto } from '../../../../shared/api/pipeex/pcfSettingsClient';
import { sanitizeMappingParameterReferenceName } from '../mappingParameterReferenceName';
import type {
  AdditionalCategoriesConfiguration,
  BoltConfiguration,
  ConnectorBindingConfiguration,
  FormField,
  MenuConfiguration,
  ObjectType,
  ProcessPropertiesConfiguration,
} from '../../../../shared/types';

export type MappingParameterSource =
  | 'mappingTable'
  | 'connector'
  | 'bolt'
  | 'process'
  | 'project'
  | 'additional'
  | 'scopeCustom';

export type { MappingParameterRepairKind };

export interface MappingProjectParameterReference {
  name: string;
  sources: MappingParameterSource[];
  repairKinds: MappingParameterRepairKind[];
}

export interface MappingProjectReadinessInput {
  objectTypes: ObjectType[];
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
  boltConfig: BoltConfiguration;
  processConfig: ProcessPropertiesConfiguration;
  additionalCategoriesConfig: AdditionalCategoriesConfiguration;
  processConditionsEditorState?: ScopeEditorPersistenceState;
  projectPropertiesEditorState?: ScopeEditorPersistenceState;
}

export function normalizeLookupName(value: string): string {
  return value.trim().toLowerCase();
}

export function getFormFieldValue(value: FormField | string | null | undefined): string {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value.value === 'string') {
    return value.value;
  }

  return '';
}

function parseJson<T>(value: string | null | undefined, fallback: T): T {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function unwrapLegacyValue(value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    'Value' in (value as Record<string, unknown>)
  ) {
    return (value as { Value?: unknown }).Value;
  }

  return value;
}

function getTextValue(value: unknown): string {
  const unwrapped = unwrapLegacyValue(value);
  return typeof unwrapped === 'string' ? unwrapped : String(unwrapped ?? '');
}

function addReference(
  refs: Map<string, MappingProjectParameterReference>,
  rawName: string | null | undefined,
  source: MappingParameterSource,
  repairKind?: MappingParameterRepairKind
): void {
  const name = sanitizeMappingParameterReferenceName(rawName);
  if (!name) {
    return;
  }

  const key = normalizeLookupName(name);
  const current =
    refs.get(key) ??
    ({
      name,
      sources: [],
      repairKinds: [],
    } satisfies MappingProjectParameterReference);

  if (!current.sources.includes(source)) {
    current.sources.push(source);
  }

  if (repairKind && !current.repairKinds.includes(repairKind)) {
    current.repairKinds.push(repairKind);
  }

  refs.set(key, current);
}

function collectScopeConfiguredParameters(
  processConfig: ProcessPropertiesConfiguration,
  editorState: ScopeEditorPersistenceState | undefined,
  fieldByRowId: Record<string, keyof ProcessPropertiesConfiguration>
): string[] {
  const deletedRowIds = new Set(editorState?.deletedRowIds ?? []);
  const values: string[] = [];

  Object.entries(fieldByRowId).forEach(([rowId, fieldName]) => {
    if (deletedRowIds.has(rowId)) {
      return;
    }

    const value = getFormFieldValue(processConfig[fieldName] as FormField | string | undefined);
    const name = sanitizeMappingParameterReferenceName(value);
    if (name) {
      values.push(name);
    }
  });

  return values;
}

function collectScopeReferences(input: {
  refs: Map<string, MappingProjectParameterReference>;
  processConfig: ProcessPropertiesConfiguration;
  editorState: ScopeEditorPersistenceState | undefined;
  fieldByRowId: Record<string, keyof ProcessPropertiesConfiguration>;
  source: 'process' | 'project';
}): void {
  collectScopeConfiguredParameters(
    input.processConfig,
    input.editorState,
    input.fieldByRowId
  ).forEach((name) => {
    addReference(input.refs, name, input.source, 'processProject');
  });

  (input.editorState?.customRows ?? []).forEach((row) => {
    addReference(input.refs, row.value, input.source, 'processProject');
    addReference(input.refs, row.value, 'scopeCustom');
  });
}

function isAdditionalCategoryMappingAttribute(
  objectTypeName: string | null | undefined,
  pcfAttributeName: string | null | undefined
): boolean {
  const objectType = String(objectTypeName ?? '').trim().toUpperCase();
  const attribute = String(pcfAttributeName ?? '').trim().toUpperCase();

  if (objectType === 'VALVE') {
    return attribute === 'TAG';
  }

  if (objectType === 'SUPPORT' || objectType === 'SUPPORTS') {
    return attribute === 'TAG' || attribute === 'SSREF';
  }

  if (objectType === 'INSTRUMENT') {
    return attribute === 'TAG';
  }

  return false;
}

function shouldValidateMappingTableAttribute(
  input: MappingProjectReadinessInput,
  objectTypeName: string | null | undefined,
  pcfAttributeName: string | null | undefined
): boolean {
  if (
    !workflowIncludesIsoOnlySetup(input.menuConfig.selectedOption) &&
    (
      isBoltProfileMappingRow(objectTypeName, pcfAttributeName) ||
      isAdditionalCategoryMappingAttribute(objectTypeName, pcfAttributeName)
    )
  ) {
    return false;
  }

  if (
    input.boltConfig.parameterDestination === 'family' &&
    isBoltProfileMappingRow(objectTypeName, pcfAttributeName)
  ) {
    return false;
  }

  return true;
}

function getBoltRepairKind(
  input: MappingProjectReadinessInput,
  objectTypeName: string | null | undefined,
  pcfAttributeName: string | null | undefined
): MappingParameterRepairKind | undefined {
  if (
    input.boltConfig.parameterDestination === 'project' &&
    isBoltProfileMappingRow(objectTypeName, pcfAttributeName)
  ) {
    return 'bolt';
  }

  return undefined;
}

export function collectBoltConfiguredParameters(boltConfig: BoltConfiguration): string[] {
  return [
    getFormFieldValue(boltConfig.boltSetActiveValue),
    getFormFieldValue(boltConfig.boltItemDescriptionValue),
    getFormFieldValue(boltConfig.boltDiaValue),
    getFormFieldValue(boltConfig.boltQuantityValue),
    getFormFieldValue(boltConfig.boltLengthValue),
    getFormFieldValue(boltConfig.boltMaterialValue),
    getFormFieldValue(boltConfig.boltStandardValue),
  ];
}

export function collectMappedParameterReferences(
  input: MappingProjectReadinessInput
): MappingProjectParameterReference[] {
  const refs = new Map<string, MappingProjectParameterReference>();

  input.objectTypes.forEach((objectType) => {
    objectType.attributes.forEach((attribute) => {
      if (String(attribute.attributeType ?? '').trim().toLowerCase() === 'formula' ||
        String(attribute.fx ?? '').trim() !== '') {
        return;
      }

      if (
        !shouldValidateMappingTableAttribute(
          input,
          objectType.name,
          attribute.pcfAttributeName
        )
      ) {
        return;
      }

      addReference(
        refs,
        attribute.revitParam,
        'mappingTable',
        isAdditionalCategoryMappingAttribute(
          objectType.name,
          attribute.pcfAttributeName
        )
          ? 'additional'
          : getBoltRepairKind(input, objectType.name, attribute.pcfAttributeName) ??
            'mapping'
      );
    });
  });

  collectConnectorBindingParameters({
    menuConfig: input.menuConfig,
    connectorConfig: input.connectorConfig,
  }).forEach((parameterName) => {
    addReference(refs, parameterName, 'connector', 'connector');
  });

  if (workflowIncludesIsoOnlySetup(input.menuConfig.selectedOption)) {
    collectBoltConfiguredParameters(input.boltConfig).forEach((parameterName) => {
      addReference(refs, parameterName, 'bolt', 'bolt');
    });
  }

  if (input.processConfig.processConditionsChecked) {
    collectScopeReferences({
      refs,
      processConfig: input.processConfig,
      editorState: input.processConditionsEditorState,
      fieldByRowId: PROCESS_CONDITIONS_FIELD_BY_EDITOR_ROW_ID,
      source: 'process',
    });
  }

  if (input.processConfig.projectPropertiesChecked) {
    collectScopeReferences({
      refs,
      processConfig: input.processConfig,
      editorState: input.projectPropertiesEditorState,
      fieldByRowId: PROJECT_PROPERTIES_FIELD_BY_EDITOR_ROW_ID,
      source: 'project',
    });
  }

  if (workflowIncludesIsoOnlySetup(input.menuConfig.selectedOption)) {
    collectAdditionalCategoryTargets(input.additionalCategoriesConfig).forEach((target) => {
      target.parameterNames.forEach((parameterName) => {
        addReference(refs, parameterName, 'additional', 'additional');
      });
    });
  }

  return Array.from(refs.values())
    .map((reference) => {
      const repairKindsSupersedingMapping = reference.repairKinds.filter(
        (repairKind) => repairKind !== 'mapping' && repairKind !== 'processProject'
      );
      return repairKindsSupersedingMapping.length > 0
        ? {
            ...reference,
            repairKinds: reference.repairKinds.filter((repairKind) => repairKind !== 'mapping'),
          }
        : reference;
    })
    .sort((left, right) => left.name.localeCompare(right.name));
}

function collectIsoProfileReferences(
  refs: Map<string, MappingProjectParameterReference>,
  isoParameters: PcfSettingsDto['isoParameters']
): void {
  const raw = parseJson<any>(isoParameters, []);
  const isoMap = Array.isArray(raw)
    ? { ALL: raw }
    : raw && typeof raw === 'object'
      ? raw as Record<string, any[]>
      : {};

  Object.values(isoMap).forEach((entries) => {
    if (!Array.isArray(entries)) {
      return;
    }

    entries.forEach((entry) => {
      const attributeType = getTextValue(
        entry?.isoType ?? entry?.IsoType ?? entry?.attributeType ?? entry?.AttributeType
      );
      const script = getTextValue(entry?.script ?? entry?.Script);
      if (attributeType.trim().toLowerCase() === 'formula' || script.trim() !== '') {
        return;
      }

      addReference(
        refs,
        getTextValue(
          entry?.parName ??
            entry?.ParName ??
            entry?.revitParameter ??
            entry?.RevitParameter
        ),
        'mappingTable'
      );
    });
  });
}

export function getBomProfileParameterGroups(bomParameters: PcfSettingsDto['bomParameters']): {
  projectParameters: any[];
  systemParameters: any[];
} {
  const raw = parseJson<any>(bomParameters, {});
  const rawArray = Array.isArray(raw) ? raw : [];

  return {
    projectParameters: Array.isArray(raw?.ProjectParameters)
      ? raw.ProjectParameters
      : rawArray.filter((entry) => String(entry?.ParameterType ?? entry?.parameterType ?? '').trim() === '1'),
    systemParameters: Array.isArray(raw?.SystemParameters)
      ? raw.SystemParameters
      : rawArray.filter((entry) => String(entry?.ParameterType ?? entry?.parameterType ?? '').trim() === '2'),
  };
}

function collectBomProfileReferences(
  refs: Map<string, MappingProjectParameterReference>,
  bomParameters: PcfSettingsDto['bomParameters']
): void {
  const { projectParameters, systemParameters } = getBomProfileParameterGroups(bomParameters);

  projectParameters.forEach((entry) => {
    addReference(
      refs,
      getTextValue(entry?.RevitParameter ?? entry?.revitParameter),
      'project',
      'processProject'
    );
  });

  systemParameters.forEach((entry) => {
    addReference(
      refs,
      getTextValue(entry?.RevitParameter ?? entry?.revitParameter),
      'process',
      'processProject'
    );
  });
}

function collectValidationProfileReferences(
  refs: Map<string, MappingProjectParameterReference>,
  validationMap: PcfSettingsDto['validationMap']
): void {
  const entries = parseJson<Array<{ key?: string; value?: string; Key?: string; Value?: string }>>(
    validationMap,
    []
  );

  entries.forEach((entry) => {
    addReference(refs, entry.value ?? entry.Value, 'mappingTable');
  });
}

export function collectMappedParameterReferencesFromSettingsDto(
  dto: PcfSettingsDto
): MappingProjectParameterReference[] {
  const refs = new Map<string, MappingProjectParameterReference>();

  collectIsoProfileReferences(refs, dto.isoParameters);
  collectBomProfileReferences(refs, dto.bomParameters);
  collectValidationProfileReferences(refs, dto.validationMap);

  return Array.from(refs.values()).sort((left, right) => left.name.localeCompare(right.name));
}
